import React from 'react';
import { ChevronDown, Clock3, RefreshCw, ServerCog } from 'lucide-react';

export const POLLING_INTERVAL_MIN_MS = 60 * 1000;
export const POLLING_INTERVAL_MAX_MS = 60 * 60 * 1000;
export const POLLING_INTERVAL_DEFAULT_MS = 5 * 60 * 1000;

const safeMin = (minValue) =>
  Math.max(POLLING_INTERVAL_MIN_MS, Number(minValue) || POLLING_INTERVAL_MIN_MS);

const PRESETS = [
  { label: '1 min', value: POLLING_INTERVAL_MIN_MS },
  { label: '5 min', value: POLLING_INTERVAL_DEFAULT_MS },
  { label: '15 min', value: 15 * 60 * 1000 },
  { label: '1 h', value: POLLING_INTERVAL_MAX_MS },
];

/**
 * O piso é PARÂMETRO, e não constante do módulo.
 *
 * O Ranking do Comercial passa 5 min, alinhado ao TTL do cache da leitura direta: abaixo disso o
 * seletor prometeria uma frequência que a arquitetura não entrega — quatro de cada cinco ciclos
 * devolveriam a mesma entrada de cache. O painel de Integrações continua com 1 min, que é o piso
 * dele; mudar a constante global para atender o Comercial alteraria aquela tela sem motivo.
 */
const clampPollingInterval = (value, minValue = POLLING_INTERVAL_MIN_MS) => {
  const parsedValue = Number(value);
  const piso = Math.max(POLLING_INTERVAL_MIN_MS, Number(minValue) || POLLING_INTERVAL_MIN_MS);

  if (!Number.isFinite(parsedValue)) {
    return Math.max(piso, POLLING_INTERVAL_DEFAULT_MS);
  }

  return Math.min(POLLING_INTERVAL_MAX_MS, Math.max(piso, parsedValue));
};

export const formatPollingInterval = (value) => {
  const safeValue = clampPollingInterval(value);
  const minutes = Math.round(safeValue / 60000);

  if (minutes >= 60) {
    return '1 hora';
  }

  return `${minutes} min`;
};

const PollingIntervalControl = ({
  value = POLLING_INTERVAL_DEFAULT_MS,
  onChange,
  onRefresh,
  disabled = false,
  refreshing = false,
  lastRefreshLabel = 'aguardando',
  variant = 'default',
  minValue = POLLING_INTERVAL_MIN_MS,
  hint,
}) => {
  const safeValue = clampPollingInterval(value, minValue);
  const minutes = Math.round(safeValue / 60000);
  const presets = PRESETS.filter((preset) => preset.value >= safeMin(minValue));

  const handleChange = (nextValue) => {
    if (onChange) {
      onChange(clampPollingInterval(nextValue, minValue));
    }
  };

  if (variant === 'compact') {
    const tooltipId = 'ranking-polling-hint';

    return (
      <section className="group relative inline-flex items-center gap-2 text-white">
        <label className="relative block min-w-[106px]">
          <span className="sr-only">Intervalo de polling</span>
          <select
            value={safeValue}
            disabled={disabled}
            onChange={(event) => handleChange(Number(event.target.value))}
            aria-describedby={tooltipId}
            className="h-11 w-full appearance-none rounded-full border border-white/[0.14] bg-white/[0.08] px-4 pr-9 text-xs font-black text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] outline-none backdrop-blur-xl transition hover:border-white/25 hover:bg-white/[0.12] focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)] disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Intervalo de polling do ranking"
          >
            {presets.map((preset) => (
              <option key={preset.value} value={preset.value} className="bg-slate-950 text-white">
                {preset.label}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/55" />
        </label>

        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={disabled || refreshing}
            className={`relative inline-flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full border text-[color:var(--accent)] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-xl transition focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-slate-950 disabled:cursor-wait ${
              refreshing
                ? 'border-[color:var(--accent)]/45 bg-[color:var(--accent)]/[0.16] shadow-[0_0_26px_color-mix(in_srgb,var(--accent)_30%,transparent)]'
                : 'border-white/[0.12] bg-white/[0.08] hover:border-white/25 hover:bg-white/[0.13]'
            } disabled:opacity-80`}
            title={refreshing ? 'Atualizando ranking...' : 'Atualizar ranking agora'}
            aria-label={refreshing ? 'Atualizando ranking' : 'Atualizar ranking agora'}
            aria-busy={refreshing}
            aria-describedby={tooltipId}
          >
            {refreshing && (
              <span className="absolute inset-0 rounded-full border border-[color:var(--accent)]/45 opacity-70 animate-[ping_1.1s_cubic-bezier(0,0,0.2,1)_infinite]" />
            )}
            {refreshing && (
              <span className="absolute inset-1 rounded-full bg-[radial-gradient(circle,color-mix(in_srgb,var(--accent)_30%,transparent),transparent_68%)]" />
            )}
            <RefreshCw className={`relative h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="sr-only">{refreshing ? 'Atualizando ranking' : 'Atualizar ranking agora'}</span>
          </button>
        )}

        <div
          id={tooltipId}
          role="tooltip"
          className="pointer-events-none absolute right-0 top-[calc(100%+10px)] z-50 w-72 translate-y-1 rounded-2xl border border-white/[0.12] bg-slate-950/95 p-4 text-left opacity-0 shadow-[0_24px_80px_rgba(2,6,23,0.45)] backdrop-blur-2xl transition duration-200 group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100"
        >
          <div className="flex items-start gap-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white/[0.08] text-[color:var(--accent)]">
              <ServerCog className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs font-black uppercase tracking-[0.16em] text-white/[0.72]">
                Polling automático
              </p>
              <p className="mt-2 text-xs leading-5 text-white/[0.56]">
                {hint || `Atualiza a cada ${formatPollingInterval(safeValue)}.`}
              </p>
              <div className="mt-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/[0.42]">
                <Clock3 className="h-3.5 w-3.5 text-[color:var(--accent)]" />
                Atualizado {lastRefreshLabel}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-5 shadow-sm">
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            'radial-gradient(circle at 18% 0%, color-mix(in srgb, var(--accent) 18%, transparent), transparent 34%), linear-gradient(135deg, color-mix(in srgb, var(--accent) 7%, transparent), transparent 58%)',
        }}
      />

      <div className="relative space-y-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3 text-[color:var(--accent)]">
              <ServerCog className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[color:var(--text-primary)]">
                Intervalo de polling do servidor
              </p>
              <p className="mt-1 max-w-xl text-xs leading-5 text-[color:var(--text-muted)]">
                Define a frequência com que o painel consulta o servidor por novas atualizações.
                O limite seguro fica entre {formatPollingInterval(safeMin(minValue))} e 1 hora.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-4 py-3 text-right">
            <div className="flex items-center justify-end gap-2 text-[color:var(--accent)]">
              <Clock3 className="h-4 w-4" />
              <span className="text-xl font-black tracking-tight text-[color:var(--text-primary)]">
                {formatPollingInterval(safeValue)}
              </span>
            </div>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:var(--text-muted)]">
              Atual
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
          <label className="relative block">
            <span className="sr-only">Intervalo de polling do servidor</span>
            <select
              value={safeValue}
              disabled={disabled}
              onChange={(event) => handleChange(Number(event.target.value))}
              className="h-11 w-full appearance-none rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 pr-10 text-sm font-semibold text-[color:var(--text-primary)] outline-none transition hover:border-[color:var(--accent)] focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)] disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Intervalo de polling do servidor"
            >
              {presets.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--text-muted)]" />
          </label>

          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={disabled || refreshing}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-4 text-sm font-semibold text-[color:var(--text-primary)] transition hover:border-[color:var(--accent)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 text-[color:var(--accent)] ${refreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--text-muted)]">
          <span>Min {formatPollingInterval(safeMin(minValue))}</span>
          <span className="text-center">{minutes} minuto{minutes === 1 ? '' : 's'}</span>
          <span className="text-right">Max 1 h</span>
        </div>
      </div>
    </section>
  );
};

export default PollingIntervalControl;
